/**
 * Hook for case quota status
 */

import { useState, useEffect, useCallback } from 'react'
import type { CaseQuotaStatus } from '../types/case-management'
import { getCaseQuota } from '../services/cases'

interface UseCaseQuotaResult {
  /** The current quota status */
  quota: CaseQuotaStatus | null
  /** Whether the data is being loaded */
  loading: boolean
  /** Error message if fetch failed */
  error: string | null
  /** Whether the user can create a new case */
  canCreateCase: boolean
  /** Number of cases remaining in quota */
  casesRemaining: number
  /** Refetch the quota status */
  refetch: () => Promise<void>
}

/**
 * Hook to fetch the current user's case quota status
 */
export function useCaseQuota(): UseCaseQuotaResult {
  const [quota, setQuota] = useState<CaseQuotaStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await getCaseQuota()
      setQuota(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch quota status')
      setQuota(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return {
    quota,
    loading,
    error,
    canCreateCase: quota?.canCreateCase ?? false,
    casesRemaining: quota?.casesRemaining ?? 0,
    refetch: fetch,
  }
}

/**
 * Helper to format quota denial reason for display
 */
export function formatQuotaDenialReason(reason: CaseQuotaStatus['denialReason']): string {
  if (!reason) return ''

  switch (reason) {
    case 'no_active_seat':
      return 'You do not have an active seat assignment'
    case 'case_limit_reached':
      return 'You have reached your case limit'
    case 'volume_limit_reached':
      return 'You have reached your data volume limit'
    case 'license_expired':
      return 'Your licence has expired'
    case 'license_inactive':
      return 'Your licence is not active'
    default:
      return 'Unable to create case'
  }
}

/**
 * Helper to format bytes for display
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2)).toString()} ${units[i] ?? ''}`
}

/**
 * Helper to calculate quota percentage used
 */
export function calculateQuotaPercentage(used: number, limit: number): number {
  if (limit <= 0) return 0
  return Math.min(100, Math.round((used / limit) * 100))
}
